import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query'
import type { QueryClient } from '@tanstack/react-query'
import { queryKeys as appQueryKeys } from '../../api/queryKeys'
import { queryKeys } from './queryKeys'
import {
  createAgentBuilderDashboardView,
  createAgentBuilderMemory,
  createAgentBuilderDashboardFromJson,
  createAgentBuilderDashboardFromPrompt,
  deleteAgentBuilderDashboardSchedule,
  deleteAgentBuilderDashboard,
  deleteAgentBuilderDashboardVersion,
  deleteAgentBuilderDashboardView,
  deleteAgentBuilderMemory,
  fetchDashboardGroupInsight,
  fetchWidgetInsight,
  getAgentBuilderAccessStatus,
  getAgentBuilderCatalog,
  getAgentBuilderClientUserEmailOptions,
  getAgentBuilderDrilldownDimensions,
  getAgentBuilderDashboard,
  getAgentBuilderDashboardSchedule,
  getAgentBuilderDashboardScheduleRecipients,
  getAgentBuilderDashboardSchedules,
  getAgentBuilderDashboardVersions,
  getAgentBuilderDashboards,
  getAgentBuilderMemories,
  getAgentBuilderSettings,
  listAgentBuilderDashboardViews,
  publishAgentBuilderDashboardVersion,
  resolveAgentBuilderChatScheduleConfirmation,
  restoreAgentBuilderDashboardVersion,
  saveAgentBuilderDashboard,
  saveAgentBuilderDashboardSchedule,
  setAgentBuilderDashboardDefaultView,
  sendAgentBuilderChatMessage,
  unpublishAgentBuilderDashboardVersion,
  updateAgentBuilderDashboard,
  updateAgentBuilderDashboardFavorite,
  updateAgentBuilderDashboardView,
  updateAgentBuilderSettings,
  updateAgentBuilderMemory,
} from './api'
import type {
  CreateAgentBuilderDashboardViewPayload,
  CreateAgentBuilderMemoryPayload,
  CreateAgentBuilderDashboardFromJsonPayload,
  CreateAgentBuilderDashboardFromPromptPayload,
  DeleteAgentBuilderDashboardPayload,
  DeleteAgentBuilderDashboardVersionPayload,
  DeleteAgentBuilderDashboardViewPayload,
  DeleteAgentBuilderMemoryPayload,
  PublishAgentBuilderDashboardVersionPayload,
  RestoreAgentBuilderDashboardVersionPayload,
  SaveAgentBuilderDashboardPayload,
  SendAgentBuilderChatMessagePayload,
  SetAgentBuilderDashboardDefaultViewPayload,
  UpdateAgentBuilderSettingsPayload,
  AgentBuilderDashboardScheduleUpsertRequest,
  UnpublishAgentBuilderDashboardVersionPayload,
  UpdateAgentBuilderDashboardPayload,
  UpdateAgentBuilderDashboardFavoritePayload,
  UpdateAgentBuilderDashboardViewPayload,
  UpdateAgentBuilderMemoryPayload,
} from './api'
import type { AgentBuilderDashboard, AgentBuilderDashboardView, AgentBuilderGroupInsightPayload, AgentBuilderWidgetInsightPayload } from './types'
import type { AgentBuilderMemory } from './types'

export function invalidateAgentBuilderDashboardConsumers(
  queryClient: QueryClient,
  clientId: string,
  platformTypeId?: number | null,
) {
  if (platformTypeId !== undefined) {
    void queryClient.invalidateQueries({
      queryKey: queryKeys.agentBuilderDashboards(clientId, platformTypeId),
    })
  }
  void queryClient.invalidateQueries({
    queryKey: queryKeys.agentBuilderDashboards(clientId, null),
  })
  void queryClient.invalidateQueries({
    queryKey: appQueryKeys.userGroups.all,
  })
}

export function useAgentBuilderCatalogQuery(clientId: string, platformTypeId: number) {
  return useQuery({
    queryKey: queryKeys.agentBuilderCatalog(clientId, platformTypeId),
    queryFn: () => getAgentBuilderCatalog(clientId, platformTypeId),
    enabled: !!clientId && !!platformTypeId,
    // The first dashboard load waits for this query to settle (it supplies the date default). One retry
    // keeps a failed catalog from holding that load through the default retry backoff (about 7s).
    retry: 1,
  })
}

export function useAgentBuilderDrilldownHierarchyQuery(clientId: string, platformTypeId: number) {
  return useQuery({
    queryKey: queryKeys.agentBuilderDrilldownHierarchy(clientId, platformTypeId),
    queryFn: () => getAgentBuilderDrilldownDimensions(clientId, platformTypeId),
    enabled: !!clientId && !!platformTypeId,
  })
}

export function useAgentBuilderAccessStatusQuery(clientId: string) {
  return useQuery({
    queryKey: queryKeys.agentBuilderAccessStatus(clientId),
    queryFn: () => getAgentBuilderAccessStatus(clientId),
    enabled: !!clientId,
  })
}

/**
 * The Agent Builder permission alone puts PracIntel in the menu. The page itself also needs a
 * connected platform and user group membership, so it explains what is missing rather than
 * showing an empty list or a 403.
 */
export function useAgentBuilderAccess(clientId: string) {
  const query = useAgentBuilderAccessStatusQuery(clientId)
  return {
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    canAccess: query.data?.canAccess === true,
  }
}

export function useAgentBuilderDashboardsQuery(clientId: string, platformTypeId?: number | null) {
  return useQuery({
    queryKey: queryKeys.agentBuilderDashboards(clientId, platformTypeId),
    queryFn: () => getAgentBuilderDashboards(clientId, platformTypeId),
    enabled: !!clientId,
  })
}

export function useAgentBuilderDashboardQuery(dashboardId: string | null | undefined, clientId: string) {
  return useQuery({
    queryKey: queryKeys.agentBuilderDashboard(clientId, dashboardId),
    queryFn: () => getAgentBuilderDashboard(dashboardId ?? '', clientId),
    enabled: !!clientId && !!dashboardId,
  })
}

export function useAgentBuilderClientUserEmailOptionsQuery(clientId: string, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.agentBuilderClientUserEmailOptions(clientId),
    queryFn: () => getAgentBuilderClientUserEmailOptions(clientId),
    enabled: enabled && !!clientId,
  })
}

export function useAgentBuilderSettingsQuery(clientId: string, enabled: boolean, includeAdminFields = false) {
  return useQuery({
    // Scope the cache by role so an admin-fetched response never lingers in the non-admin
    // cache after a session-role transition. Combined with the queryFn strip below, the
    // non-admin cache entry never contains `impersonationEmail` at all.
    queryKey: [...queryKeys.agentBuilderSettings(clientId), includeAdminFields ? 'admin' : 'nonadmin'],
    queryFn: async () => {
      const settings = await getAgentBuilderSettings(clientId)
      // Defense in depth: even though the API strips `impersonationEmail` for non-admin
      // sessions, sanitize before writing to the react-query cache so the field can never
      // sit in cache for a non-admin caller (select() only transforms hook output).
      return includeAdminFields ? settings : { ...settings, impersonationEmail: null }
    },
    enabled: enabled && !!clientId,
  })
}

export function useAgentBuilderUpdateSettingsMutation(clientId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: UpdateAgentBuilderSettingsPayload) => updateAgentBuilderSettings(payload),
    onSuccess: (settings) => {
      // Invalidate both role-scoped cache entries — the concrete keys are set by
      // useAgentBuilderSettingsQuery. A refetch will re-sanitize per role.
      void queryClient.invalidateQueries({ queryKey: queryKeys.agentBuilderSettings(clientId) })
      invalidateAgentBuilderDashboardConsumers(queryClient, clientId, null)
      void queryClient.invalidateQueries({
        queryKey: ['dashboardBuilder'],
      })
      void settings
    },
  })
}

export function useAgentBuilderSaveDashboardMutation(clientId: string, dashboardId?: string | null) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: SaveAgentBuilderDashboardPayload) => saveAgentBuilderDashboard(payload),
    onSuccess: (dashboard) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.agentBuilderDashboard(clientId, dashboard.dashboardId),
      })
      void queryClient.invalidateQueries({
        queryKey: queryKeys.agentBuilderDashboardVersions(clientId, dashboardId ?? dashboard.dashboardId),
      })
      invalidateAgentBuilderDashboardConsumers(queryClient, clientId, dashboard.platformTypeId)
    },
  })
}

export function useAgentBuilderUpdateDashboardMutation(clientId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: UpdateAgentBuilderDashboardPayload) => updateAgentBuilderDashboard(payload),
    onSuccess: (dashboard) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.agentBuilderDashboard(clientId, dashboard.dashboardId),
      })
      invalidateAgentBuilderDashboardConsumers(queryClient, clientId, dashboard.platformTypeId)
    },
  })
}

export function useAgentBuilderDashboardViewsQuery(clientId: string, dashboardRootId: string | null | undefined, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.agentBuilderDashboardViews(clientId, dashboardRootId),
    queryFn: () => listAgentBuilderDashboardViews(clientId, dashboardRootId ?? ''),
    enabled: enabled && !!clientId && !!dashboardRootId,
  })
}

// Keeps the cached list in the order the API returns it: default first, then by name.
function sortDashboardViews(views: AgentBuilderDashboardView[]) {
  return [...views].sort((left, right) => (
    Number(right.isDefault) - Number(left.isDefault)
    || left.name.localeCompare(right.name, undefined, { sensitivity: 'base' })
  ))
}

// The dashboard GET carries the caller's default view, which the viewer applies on open, so it is
// refreshed with the views list. Matched by client so a dashboard cached under a version id is included.
function invalidateDashboardViewQueries(queryClient: QueryClient, clientId: string, dashboardId: string) {
  void queryClient.invalidateQueries({ queryKey: queryKeys.agentBuilderDashboardViews(clientId, dashboardId) })
  void queryClient.invalidateQueries({ queryKey: queryKeys.agentBuilderDashboard(clientId).slice(0, 3) })
}

export function useAgentBuilderCreateDashboardViewMutation(clientId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: CreateAgentBuilderDashboardViewPayload) => createAgentBuilderDashboardView(payload),
    onSuccess: (view, payload) => {
      queryClient.setQueryData<AgentBuilderDashboardView[]>(
        queryKeys.agentBuilderDashboardViews(clientId, payload.dashboardId),
        (current = []) => sortDashboardViews([
          ...current.map((item) => (view.isDefault ? { ...item, isDefault: false } : item)),
          view,
        ]),
      )
    },
    onSettled: (_view, _error, payload) => {
      invalidateDashboardViewQueries(queryClient, clientId, payload.dashboardId)
    },
  })
}

export function useAgentBuilderUpdateDashboardViewMutation(clientId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: UpdateAgentBuilderDashboardViewPayload) => updateAgentBuilderDashboardView(payload),
    onSuccess: (view, payload) => {
      queryClient.setQueryData<AgentBuilderDashboardView[]>(
        queryKeys.agentBuilderDashboardViews(clientId, payload.dashboardId),
        (current = []) => sortDashboardViews(current.map((item) => {
          if (item.viewId === view.viewId) return view
          return view.isDefault ? { ...item, isDefault: false } : item
        })),
      )
    },
    onSettled: (_view, _error, payload) => {
      invalidateDashboardViewQueries(queryClient, clientId, payload.dashboardId)
    },
  })
}

export function useAgentBuilderDeleteDashboardViewMutation(clientId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: DeleteAgentBuilderDashboardViewPayload) => deleteAgentBuilderDashboardView(payload),
    onMutate: async (payload) => {
      const listKey = queryKeys.agentBuilderDashboardViews(clientId, payload.dashboardId)
      await queryClient.cancelQueries({ queryKey: listKey })
      const previousList = queryClient.getQueryData<AgentBuilderDashboardView[]>(listKey)
      queryClient.setQueryData<AgentBuilderDashboardView[]>(listKey, (current = []) =>
        current.filter((view) => view.viewId !== payload.viewId),
      )
      return { previousList }
    },
    onError: (_error, payload, context) => {
      if (context?.previousList) {
        queryClient.setQueryData(queryKeys.agentBuilderDashboardViews(clientId, payload.dashboardId), context.previousList)
      }
    },
    onSettled: (_result, _error, payload) => {
      invalidateDashboardViewQueries(queryClient, clientId, payload.dashboardId)
    },
  })
}

export function useAgentBuilderSetDefaultDashboardViewMutation(clientId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: SetAgentBuilderDashboardDefaultViewPayload) => setAgentBuilderDashboardDefaultView(payload),
    onMutate: async (payload) => {
      const listKey = queryKeys.agentBuilderDashboardViews(clientId, payload.dashboardId)
      await queryClient.cancelQueries({ queryKey: listKey })
      const previousList = queryClient.getQueryData<AgentBuilderDashboardView[]>(listKey)
      queryClient.setQueryData<AgentBuilderDashboardView[]>(listKey, (current = []) =>
        sortDashboardViews(current.map((view) => ({ ...view, isDefault: view.viewId === payload.viewId }))),
      )
      return { previousList }
    },
    onSuccess: (views, payload) => {
      queryClient.setQueryData(queryKeys.agentBuilderDashboardViews(clientId, payload.dashboardId), views)
    },
    onError: (_error, payload, context) => {
      if (context?.previousList) {
        queryClient.setQueryData(queryKeys.agentBuilderDashboardViews(clientId, payload.dashboardId), context.previousList)
      }
    },
    onSettled: (_result, _error, payload) => {
      invalidateDashboardViewQueries(queryClient, clientId, payload.dashboardId)
    },
  })
}

export function useAgentBuilderUpdateDashboardFavoriteMutation(clientId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: UpdateAgentBuilderDashboardFavoritePayload) => updateAgentBuilderDashboardFavorite(payload),
    onMutate: async (payload) => {
      const listKey = queryKeys.agentBuilderDashboards(clientId, null)
      await queryClient.cancelQueries({ queryKey: listKey })
      const previousList = queryClient.getQueryData<AgentBuilderDashboard[]>(listKey)

      queryClient.setQueryData<AgentBuilderDashboard[]>(listKey, (current = []) =>
        current.map((dashboard) => {
          const rootId = dashboard.rootDashboardId ?? dashboard.rootId ?? dashboard.dashboardId
          return rootId === payload.dashboardId ? { ...dashboard, isFavorite: payload.isFavorite } : dashboard
        }),
      )

      return { previousList }
    },
    onError: (_error, _payload, context) => {
      if (context?.previousList) {
        queryClient.setQueryData(queryKeys.agentBuilderDashboards(clientId, null), context.previousList)
      }
    },
    onSettled: (dashboard) => {
      invalidateAgentBuilderDashboardConsumers(queryClient, clientId, null)
      if (dashboard) {
        void queryClient.invalidateQueries({
          queryKey: queryKeys.agentBuilderDashboard(clientId, dashboard.dashboardId),
        })
        invalidateAgentBuilderDashboardConsumers(queryClient, clientId, dashboard.platformTypeId)
      }
    },
  })
}

export function useAgentBuilderCreateDashboardFromPromptMutation() {
  return useMutation({
    mutationFn: (payload: CreateAgentBuilderDashboardFromPromptPayload) => createAgentBuilderDashboardFromPrompt(payload),
  })
}

export function useAgentBuilderCreateDashboardFromJsonMutation(clientId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: CreateAgentBuilderDashboardFromJsonPayload) => createAgentBuilderDashboardFromJson(payload),
    onSuccess: (dashboard) => {
      invalidateAgentBuilderDashboardConsumers(queryClient, clientId, dashboard.platformTypeId)
    },
  })
}

export function useAgentBuilderSendChatMessageMutation() {
  return useMutation({
    mutationFn: (payload: SendAgentBuilderChatMessagePayload) => sendAgentBuilderChatMessage(payload),
  })
}

export function useAgentBuilderDashboardVersionsQuery(clientId: string, dashboardId?: string | null) {
  return useQuery({
    queryKey: queryKeys.agentBuilderDashboardVersions(clientId, dashboardId),
    queryFn: () => getAgentBuilderDashboardVersions(clientId, dashboardId),
    enabled: !!clientId && !!dashboardId,
    staleTime: 60 * 1000,
  })
}

export function useAgentBuilderDashboardSchedulesQuery(clientId: string, dashboardRootId?: string | null) {
  return useQuery({
    queryKey: queryKeys.agentBuilderDashboardSchedules(clientId, dashboardRootId),
    queryFn: () => getAgentBuilderDashboardSchedules(clientId, dashboardRootId ?? ''),
    enabled: !!clientId && !!dashboardRootId,
  })
}

export function useAgentBuilderDashboardScheduleQuery(scheduleId?: string | null) {
  return useQuery({
    queryKey: ['dashboardBuilder', 'agentBuilderDashboardSchedule', scheduleId ?? null] as const,
    queryFn: () => getAgentBuilderDashboardSchedule(scheduleId ?? ''),
    enabled: !!scheduleId,
  })
}

export function useAgentBuilderDashboardScheduleRecipientsQuery(clientId: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.agentBuilderDashboardScheduleRecipients(clientId),
    queryFn: () => getAgentBuilderDashboardScheduleRecipients(clientId),
    enabled: enabled && !!clientId,
  })
}

export function useAgentBuilderSaveDashboardScheduleMutation(clientId: string, dashboardRootId?: string | null) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: AgentBuilderDashboardScheduleUpsertRequest) => saveAgentBuilderDashboardSchedule(payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.agentBuilderDashboardSchedules(clientId, dashboardRootId),
      })
    },
  })
}

export function useAgentBuilderDeleteDashboardScheduleMutation(clientId: string, dashboardRootId?: string | null) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (scheduleId: string) => deleteAgentBuilderDashboardSchedule(scheduleId),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.agentBuilderDashboardSchedules(clientId, dashboardRootId),
      })
    },
  })
}

export function useAgentBuilderMemoriesQuery(clientId: string, rootDashboardId?: string | null) {
  return useQuery({
    queryKey: queryKeys.agentBuilderMemories(clientId, rootDashboardId),
    queryFn: () => getAgentBuilderMemories(clientId, rootDashboardId),
    enabled: !!clientId,
  })
}

export function useAgentBuilderCreateMemoryMutation(clientId: string, rootDashboardId?: string | null) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: CreateAgentBuilderMemoryPayload) => createAgentBuilderMemory(payload),
    onSuccess: (memory) => {
      queryClient.setQueryData<AgentBuilderMemory[]>(
        queryKeys.agentBuilderMemories(clientId, rootDashboardId),
        (current = []) => [memory, ...current.filter((item) => item.id !== memory.id)],
      )
      void queryClient.invalidateQueries({
        queryKey: queryKeys.agentBuilderMemories(clientId, rootDashboardId),
      })
    },
  })
}

export function useAgentBuilderUpdateMemoryMutation(clientId: string, rootDashboardId?: string | null) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: UpdateAgentBuilderMemoryPayload) => updateAgentBuilderMemory(payload),
    onSuccess: (memory) => {
      queryClient.setQueryData<AgentBuilderMemory[]>(
        queryKeys.agentBuilderMemories(clientId, rootDashboardId),
        (current = []) => current.map((item) => (item.id === memory.id ? memory : item)),
      )
      void queryClient.invalidateQueries({
        queryKey: queryKeys.agentBuilderMemories(clientId, rootDashboardId),
      })
    },
  })
}

export function useAgentBuilderDeleteMemoryMutation(clientId: string, rootDashboardId?: string | null) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: DeleteAgentBuilderMemoryPayload) => deleteAgentBuilderMemory(payload),
    onSuccess: (_result, payload) => {
      queryClient.setQueryData<AgentBuilderMemory[]>(
        queryKeys.agentBuilderMemories(clientId, rootDashboardId),
        (current = []) => current.filter((item) => item.id !== payload.memoryId),
      )
      void queryClient.invalidateQueries({
        queryKey: queryKeys.agentBuilderMemories(clientId, rootDashboardId),
      })
    },
  })
}

export function useAgentBuilderRestoreDashboardVersionMutation(clientId: string, dashboardId?: string | null) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: RestoreAgentBuilderDashboardVersionPayload) => restoreAgentBuilderDashboardVersion(payload),
    onSuccess: (dashboard) => {
      const restoredDashboardId = dashboard.dashboardId ?? dashboard.rootId ?? dashboardId
      void queryClient.invalidateQueries({
        queryKey: queryKeys.agentBuilderDashboard(clientId, restoredDashboardId),
      })
      void queryClient.invalidateQueries({
        queryKey: queryKeys.agentBuilderDashboardVersions(clientId, restoredDashboardId),
      })
      invalidateAgentBuilderDashboardConsumers(queryClient, clientId, dashboard.platformTypeId)
    },
  })
}

export function useAgentBuilderPublishDashboardVersionMutation(clientId: string, dashboardId?: string | null) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: PublishAgentBuilderDashboardVersionPayload) => publishAgentBuilderDashboardVersion(payload),
    onSuccess: (dashboard) => {
      const restoredDashboardId = dashboard.dashboardId ?? dashboard.rootId ?? dashboardId
      void queryClient.invalidateQueries({
        queryKey: queryKeys.agentBuilderDashboard(clientId, restoredDashboardId),
      })
      void queryClient.invalidateQueries({
        queryKey: queryKeys.agentBuilderDashboardVersions(clientId, restoredDashboardId),
      })
      invalidateAgentBuilderDashboardConsumers(queryClient, clientId, dashboard.platformTypeId)
    },
  })
}

export function useAgentBuilderUnpublishDashboardVersionMutation(clientId: string, dashboardId?: string | null) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: UnpublishAgentBuilderDashboardVersionPayload) => unpublishAgentBuilderDashboardVersion(payload),
    onSuccess: (dashboard) => {
      const restoredDashboardId = dashboard.dashboardId ?? dashboard.rootId ?? dashboardId
      void queryClient.invalidateQueries({
        queryKey: queryKeys.agentBuilderDashboard(clientId, restoredDashboardId),
      })
      void queryClient.invalidateQueries({
        queryKey: queryKeys.agentBuilderDashboardVersions(clientId, restoredDashboardId),
      })
      invalidateAgentBuilderDashboardConsumers(queryClient, clientId, dashboard.platformTypeId)
    },
  })
}

export function useAgentBuilderDeleteDashboardVersionMutation(clientId: string, dashboardId?: string | null) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: DeleteAgentBuilderDashboardVersionPayload) => deleteAgentBuilderDashboardVersion(payload),
    onSuccess: (_result, payload) => {
      const restoredDashboardId = payload.dashboardId ?? dashboardId
      queryClient.setQueryData<AgentBuilderDashboard[]>(
        queryKeys.agentBuilderDashboardVersions(clientId, dashboardId),
        (current = []) => current.filter((item) => item.id !== payload.versionId && item.versionId !== payload.versionId),
      )
      void queryClient.invalidateQueries({
        queryKey: queryKeys.agentBuilderDashboard(clientId, restoredDashboardId),
      })
      void queryClient.invalidateQueries({
        queryKey: queryKeys.agentBuilderDashboardVersions(clientId, dashboardId),
      })
      if (payload.platformTypeId) {
        invalidateAgentBuilderDashboardConsumers(queryClient, clientId, payload.platformTypeId)
      } else {
        invalidateAgentBuilderDashboardConsumers(queryClient, clientId, null)
      }
    },
  })
}

export function useAgentBuilderDeleteDashboardMutation(clientId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: DeleteAgentBuilderDashboardPayload) => deleteAgentBuilderDashboard(payload),
    onSuccess: (_result, payload) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.agentBuilderDashboard(clientId, payload.rootId),
      })
      void queryClient.invalidateQueries({
        queryKey: queryKeys.agentBuilderDashboardVersions(clientId, payload.rootId),
      })
      invalidateAgentBuilderDashboardConsumers(queryClient, clientId, payload.platformTypeId ?? null)
    },
  })
}

export function useAgentBuilderWidgetInsights(
  payloads: AgentBuilderWidgetInsightPayload[],
  enabled: boolean,
  signature?: string | null,
) {
  return useQueries({
    queries: payloads.map((payload) => ({
      queryKey: queryKeys.agentBuilderWidgetInsight(payload.agentBuilderDashboardRootId, payload.widgetId, signature),
      queryFn: () => fetchWidgetInsight(payload),
      enabled: enabled && !!payload.agentBuilderDashboardRootId,
      staleTime: 5 * 60 * 1000,
    })),
  })
}

export function useAgentBuilderGroupInsight(
  payload: AgentBuilderGroupInsightPayload | null,
  enabled: boolean,
  signature?: string | null,
) {
  return useQuery({
    queryKey: queryKeys.agentBuilderGroupInsight(payload?.agentBuilderDashboardRootId ?? '', signature),
    queryFn: () => fetchDashboardGroupInsight(payload!),
    enabled: enabled && !!payload?.agentBuilderDashboardRootId && (payload?.widgetInsights?.length ?? 0) > 0,
    staleTime: 5 * 60 * 1000,
  })
}

export function useAgentBuilderResolveScheduleConfirmationMutation(clientId: string, dashboardRootId?: string | null) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ chatId, accept }: { chatId: string; accept: boolean }) =>
      resolveAgentBuilderChatScheduleConfirmation(chatId, accept),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.agentBuilderDashboardSchedules(clientId, dashboardRootId),
      })
    },
  })
}
